import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from '@ludolio/shared-ui';
import { Mail, MessageCircle, Headphones, ExternalLink } from 'lucide-react';
import { LUDOLIO_SDKS } from '@/components/SdkLinks';

export const Route = createFileRoute('/_authenticated/support')({
  component: SupportPage,
});

function SupportPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Support</h1>
        <p className="text-muted-foreground mt-1">
          Get help with your developer account, games, or platform questions.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="flex flex-col">
          <CardHeader className="flex flex-row items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--color-primary))]/12 flex items-center justify-center">
              <Headphones className="h-5 w-5 text-[hsl(var(--color-primary))]" />
            </div>
            <div>
              <CardTitle>Marcus</CardTitle>
              <CardDescription>Developer Relations</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 space-y-3">
            <p className="text-sm text-muted-foreground">
              Reach out to discuss whether your game is suitable for the platform, understand your needs, or talk contracts and partnerships.
            </p>
            <div className="mt-auto">
              <Button variant="outline" size="sm" asChild className="w-full">
                <a href="mailto:marcus@ludolio.com">
                  <Mail className="h-4 w-4 mr-2" />
                  marcus@ludolio.com
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="flex flex-row items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--color-primary))]/12 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-[hsl(var(--color-primary))]" />
            </div>
            <div>
              <CardTitle>Ben</CardTitle>
              <CardDescription>Developer Relations</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 space-y-3">
            <p className="text-sm text-muted-foreground">
              Reach out for onboarding help, partnership inquiries, or general platform guidance.
            </p>
            <div className="mt-auto">
              <Button variant="outline" size="sm" asChild className="w-full">
                <a href="mailto:ben@ludolio.com">
                  <Mail className="h-4 w-4 mr-2" />
                  ben@ludolio.com
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="flex flex-row items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--color-primary))]/12 flex items-center justify-center">
              <Mail className="h-5 w-5 text-[hsl(var(--color-primary))]" />
            </div>
            <div>
              <CardTitle>Marvin</CardTitle>
              <CardDescription>Technical & Engineering Support</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 space-y-3">
            <p className="text-sm text-muted-foreground">
              Reach out for technical issues, SDK questions, build uploads, or bug reports.
            </p>
            <div className="mt-auto">
              <Button variant="outline" size="sm" asChild className="w-full">
                <a href="mailto:marvin@ludolio.com">
                  <Mail className="h-4 w-4 mr-2" />
                  marvin@ludolio.com
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3">
          <ExternalLink className="h-6 w-6 text-[hsl(var(--color-primary))]" />
          <div>
            <CardTitle>Other Resources</CardTitle>
            <CardDescription>Self-service links and documentation</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <Link to="/guide" className="underline text-foreground">Developer Guide</Link> —
              step-by-step instructions for submitting a game.
            </li>
            {LUDOLIO_SDKS.map((sdk) => (
              <li key={sdk.name}>
                <a href={sdk.href} target="_blank" rel="noreferrer" className="underline text-foreground">
                  {sdk.name} <ExternalLink className="inline h-3 w-3" />
                </a>
                {' '}— {sdk.platforms}.
              </li>
            ))}
            <li>
              <Link to="/getting-started" className="underline text-foreground">Getting Started</Link> —
              packaging, uploads, and testing checklist.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
